import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, Plus, Images, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBgLibraryAdmin, saveBgLibrary } from '../../services/site';
import { uploadMediaFiles } from '../../services/media';
import { ImageLibrary } from '../../components/admin/ImageLibrary';

const THEMES = [
  { key: 'dark', label: 'Fonds — Mode nuit (thème sombre)' },
  { key: 'light', label: 'Fonds — Mode éclairé (thème clair)' },
];

const BgLibrary = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lib, setLib] = useState({ light: [], dark: [], interval: 15 });
  const [libOpen, setLibOpen] = useState(false);
  const [libTarget, setLibTarget] = useState('dark');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchBgLibraryAdmin(token);
        setLib({ light: [], dark: [], interval: 15, ...data });
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const addImage = (theme, url) => {
    if (!url) return;
    setLib((l) => ({ ...l, [theme]: [...(l[theme] || []), url] }));
  };
  const addImages = (theme, urls) => {
    if (!urls || !urls.length) return;
    setLib((l) => ({ ...l, [theme]: [...(l[theme] || []), ...urls] }));
  };
  const removeImage = (theme, idx) => setLib((l) => ({ ...l, [theme]: (l[theme] || []).filter((_, i) => i !== idx) }));

  const onUpload = async (theme, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      setUploading(theme); setError(''); setSuccess('');
      const res = await uploadMediaFiles(files, token);
      const urls = (res?.uploaded || []).map((u) => u.url).filter(Boolean);
      addImages(theme, urls);
      if (res?.errors?.length) {
        setError(`${res.errors.length} fichier(s) refusé(s) : ${res.errors.map((e) => e.name).join(', ')}`);
      } else if (urls.length > 1) {
        setSuccess(`✅ ${urls.length} photos ajoutées. N'oubliez pas de sauvegarder.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading('');
    }
  };

  const onSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      await saveBgLibrary(lib, token);
      setSuccess('✅ Bibliothèque de fonds enregistrée. Les pages tirent désormais au hasard parmi ces photos.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="bg-library-page">
      <Button asChild variant="ghost" size="sm"><Link to="/admin/backgrounds"><ArrowLeft className="h-4 w-4 mr-2" />Fonds d'écran</Link></Button>
      <Card>
        <CardHeader>
          <CardTitle>Bibliothèque de fonds des pages</CardTitle>
          <CardDescription>Choisissez ici les photos autorisées comme fond des pages. Chaque page en affiche une <strong>au hasard</strong> et en change automatiquement. Cette bibliothèque n'est accessible qu'au fondateur.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && <Alert><AlertDescription className="text-red-600" data-testid="lib-error">{error}</AlertDescription></Alert>}
          {success && <Alert><AlertDescription className="text-green-600" data-testid="lib-success">{success}</AlertDescription></Alert>}

          <div className="rounded-lg border border-border p-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Changer de photo toutes les</label>
            <input
              type="number" min="0" max="600"
              data-testid="lib-interval"
              value={lib.interval != null ? lib.interval : 15}
              onChange={(e) => setLib((l) => ({ ...l, interval: Math.max(0, Math.min(600, Number(e.target.value) || 0)) }))}
              className="w-24 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <span className="text-sm text-muted-foreground">secondes (0 = une seule au hasard par visite). Défaut : 15 s.</span>
          </div>

          {THEMES.map(({ key, label }) => {
            const images = lib[key] || [];
            return (
              <div key={key} className="rounded-lg border border-border p-4 space-y-3">
                <p className="text-sm font-semibold">{label} <span className="text-muted-foreground font-normal">({images.length})</span></p>
                {images.length === 0 && <p className="text-xs text-muted-foreground">Aucune photo — ce thème utilisera le fond par défaut.</p>}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {images.map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded-md overflow-hidden border border-border">
                      <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                      <button type="button" onClick={() => removeImage(key, idx)} data-testid={`lib-remove-${key}-${idx}`} className="absolute top-1 right-1 z-10 rounded-full bg-black/60 text-white p-1 hover:bg-red-600"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button asChild variant="outline" size="sm" data-testid={`lib-upload-btn-${key}`}>
                    <label className="cursor-pointer"><Upload className="h-4 w-4 mr-1" />Téléverser (plusieurs)
                      <input type="file" accept="image/*" multiple className="hidden" data-testid={`lib-upload-${key}`} onChange={(e) => onUpload(key, e.target.files)} />
                    </label>
                  </Button>
                  {uploading === key && <span className="text-xs text-muted-foreground">Téléversement…</span>}
                  <Button type="button" size="sm" variant="outline" data-testid={`lib-pick-${key}`} onClick={() => { setLibTarget(key); setLibOpen(true); }}><Images className="h-4 w-4 mr-1" />Choisir dans mes photos</Button>
                  <div className="flex items-center gap-1">
                    <Input id={`lib-urlinput-${key}`} data-testid={`lib-url-${key}`} placeholder="Coller une URL…" className="text-xs w-56" onKeyDown={(e) => { if (e.key === 'Enter') { addImage(key, e.target.value.trim()); e.target.value = ''; } }} />
                    <Button type="button" size="sm" variant="outline" data-testid={`lib-addurl-${key}`} onClick={() => { const el = document.getElementById(`lib-urlinput-${key}`); if (el?.value.trim()) { addImage(key, el.value.trim()); el.value = ''; } }}><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex justify-end">
            <Button data-testid="lib-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Enregistrement…' : 'Sauvegarder la bibliothèque'}</Button>
          </div>
        </CardContent>
      </Card>

      <ImageLibrary open={libOpen} onOpenChange={setLibOpen} onSelect={(url) => addImage(libTarget, url)} />
    </div>
  );
};

export default BgLibrary;
